import {useArticleWorkspace} from './useArticleWorkspace.js';
import {readLoginIntent} from './articleWorkspace.js';
import {useArticleDraft} from './useArticleDraft.js';
import {requestJSON} from './apiRequest.js';
import {responseShapeFor, createLatestRequest} from './requestContract.js';
import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import ConceptLinks,{ConceptMatches} from './ConceptLinks.jsx';
import {categoryMatches,activeCategories,categoryAssignable,initialCategoryNames,classificationSummary} from './categories.js';
import ArticleInfobox from './ArticleInfobox.jsx';
import PollPanel from './PollPanel.jsx';
import VotePanelResize,{readVoteWidth} from './VotePanelResize.jsx';
import {usePolls} from './usePolls.js';
import {projectThreadDeletion} from './threadCascadeProjection.js';
import NameProposals,{useNameProposals} from './NameProposals.jsx';
import {quoteLocation} from './quoteLocation.js';
import Modal from './components/Modal.jsx';
import PanelResize,{readPanelWidth} from './PanelResize.jsx';
import WikiChatMark from './components/WikiChatMark.jsx';
const AboutPage = React.lazy(() => import('./AboutPage.jsx'));
const AdminPage = React.lazy(() => import('./AdminPage.jsx'));
import ReviewPanel from './ReviewPanel.jsx';
import ResolutionForm from './ResolutionForm.jsx';
import LoginDialog from './LoginDialog.jsx';
import ChatPanel from './ChatPanel.jsx';
import DiscussionToPoll from './DiscussionToPoll.jsx';
import ThreadManagement,{useThreadIssueLinks} from './ThreadManagement.jsx';
import IssueManagement from './IssueManagement.jsx';
import PersonalMenu, { ContributionsPanel } from './PersonalMenu.jsx';
import { normalizeName, termNames, resolveNames } from './termNames.js';
import { WikiContent } from './Wikitext.jsx';
let editorLoad;
function preloadEditor() {
  if (!editorLoad) editorLoad = import('./WikiEditor.jsx').catch(error => { editorLoad = null; throw error; });
  return editorLoad;
}
const DeferredWikiEditor = React.lazy(preloadEditor);
import { wikiSections, wikiVisibleRange } from './wikitext.js';
import './wikitext.css';
import Notifications from './Notifications.jsx';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { Sparkles, PanelLeftOpen, PanelLeftClose, MessageCircle, ArrowUpRight, ArrowLeft, Plus, LogIn, Pencil, History, X, Check, CheckCheck, Link as LinkIcon, Info, TextSelect, FileText, LoaderCircle, Search, ListFilter } from 'lucide-react';
import { buildAnchor, locateAnchor, fieldNames } from './domain.js';
import Directory from './Directory.jsx';
import TermNavigation from './TermNavigation.jsx';
import {adjacentTerms} from './termNavigation.js';
import { versionFields } from './versionFields.js';
const VersionHistory = React.lazy(() => import('./VersionHistory.jsx'));
import DeferredSection from './components/DeferredSection.jsx';
import { revealMessage } from './MessageBody.jsx';
import { rootFor,indexDiscussion } from './discussionModel.js';
import EnglishReference from './EnglishReference.jsx';
import { defaultBody, parseBody } from './articleStructure.js';
import './styles.css';
import './wiki.css';
import './theme.css';
import './design.css';
import './collaboration.css';
import './mobile.css';
import './revisions.css';
import './editor-integrated.css';
import './conversation-panel.css';
import './chat-panel.css';
import './login.css';
import './surfaces.css';
import './editor-surface.css';
import './three-pane.css';
import './ai-navigation.css';
import './discussion-workspace.css';
import './workspace-motion.css';
import { useTheme } from './theme.js';
import { readDrafts } from './commentDrafts.js';
const sessionKey = 'wiki-chat-session:' + new URL('./', location.href).pathname;
function storedSession() {
  try {
    return localStorage.getItem(sessionKey) || '';
  } catch {
    return '';
  }
}

const formatTime = s => s ? new Intl.DateTimeFormat('zh-TW', {
  month: 'numeric',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
}).format(new Date(s)) : '初始匯入';
function App() {
  const {
    preference,
    chooseTheme
  } = useTheme();
  const [editError,setEditError]=useState('');
  const pendingNavigation=useRef(null), editorExit=useRef(null);
  const termArrival=useRef(null);
  const [leaveRequest,setLeaveRequest]=useState(0);
  const [savedEdit,setSavedEdit]=useState(null);
  const [discussionWidth,setDiscussionWidth]=useState(readPanelWidth);
  const [discussionDetailHost,setDiscussionDetailHost]=useState(null);
  const [voteSource,setVoteSource]=useState(null);
  const [discussionVoteSources,setDiscussionVoteSources]=useState([]);
  const [voteWidth,setVoteWidth]=useState(readVoteWidth);
  useEffect(()=>{try{localStorage.setItem('wiki-chat-vote-width',String(voteWidth));}catch{}},[voteWidth]);
  const returnLocation=useRef(null);
  const [nameReveal,setNameReveal]=useState(null);
  const [pollScope,setPollScope]=useState({termId:null,id:'names'});
  const [pollReveal,setPollReveal]=useState(null),[pollFocus,setPollFocus]=useState(null);
  const [pendingPoll,setPendingPoll]=useState(()=>readLoginIntent(sessionStorage,'poll'));
  const [pollResume,setPollResume]=useState(0);
  function loginForPoll(intent){workspace.requireLogin('poll',intent);}

  const [nameDiscussionFocus,setNameDiscussionFocus]=useState(null);
  const [nameProposeRequest,setNameProposeRequest]=useState(0);
  const [pendingName,setPendingName]=useState(()=>readLoginIntent(sessionStorage,'name'));
  function loginForName(intent){if(intent==='propose-name')workspace.requireLogin('name');else setModal('login');}
  const [pendingOpinion,setPendingOpinion]=useState(()=>readLoginIntent(sessionStorage,'opinion'));
  function clearOpinionIntent(){workspace.clearIntent('opinion');}
  const [namesOpen,setNamesOpen]=useState(false);
  const [mobilePane,setMobilePane]=useState('article');
  const workspaceViewport=useRef(null);
  const [compactTitle,setCompactTitle]=useState(false);
  const editorFocus=useRef(null);
  const [screenWidth,setScreenWidth]=useState(window.innerWidth);
  const namesRef=useRef(null);
  const discussionToggleRef=useRef(null),pollToggleRef=useRef(null),articleTabRef=useRef(null),pollHeadingRef=useRef(null),pollReturnRef=useRef(null);
  const [pollFocusRequest,setPollFocusRequest]=useState(null);
  useEffect(()=>{const update=()=>{const focused=document.activeElement;if(window.innerWidth<=900&&focused?.closest('.directory-filters'))setDirectoryTool(focused.closest('.directory-search')?'search':'category');setScreenWidth(window.innerWidth);};window.addEventListener('resize',update);return()=>window.removeEventListener('resize',update);},[]);
  useEffect(()=>{try{if(discussionWidth===null)localStorage.removeItem('wiki-chat-discussion-width');else localStorage.setItem('wiki-chat-discussion-width',String(discussionWidth));}catch{}},[discussionWidth]);
  const [contributions, setContributions] = useState(null);
  const [remote, setRemote] = useState(null),
    [error, setError] = useState(''),
    [selectedId, setSelectedId] = useState(new URLSearchParams(location.search).get('term') || null);
  const [directoryDisclosure,setDirectoryDisclosure]=useState({collapsed:{},filteredCollapse:{context:'',groups:[]}});
  const [directoryTool,setDirectoryTool]=useState(null);
  const [directoryToolsHost,setDirectoryToolsHost]=useState(null);
  const [directoryArranging,setDirectoryArranging]=useState(false);
  const directorySearchTrigger=useRef(null),directoryCategoryTrigger=useRef(null);
  const directoryDefaults=useRef(null);
  useEffect(()=>{
    if(!remote?.categories)return;
    const next=Object.fromEntries(remote.categories.items.map(c=>[c.id,Boolean(c.notOpen)]));
    const previous=directoryDefaults.current;
    directoryDefaults.current=next;
    setDirectoryDisclosure(current=>{
      const collapsed=Object.fromEntries(Object.entries(current.collapsed).filter(([id])=>id in next&&(!previous||previous[id]===next[id])));
      const groups=current.filteredCollapse.groups.filter(id=>id in next);
      if(Object.keys(collapsed).length===Object.keys(current.collapsed).length&&groups.length===current.filteredCollapse.groups.length)return current;
      return {collapsed,filteredCollapse:{...current.filteredCollapse,groups}};
    });
  },[remote?.categories]);
  const [discussionOpen, setDiscussionOpen] = useState(false),
    [selectionPoint, setSelectionPoint] = useState(null),
    [query, setQuery] = useState(new URLSearchParams(location.search).get('q') || ''),
    [category, setCategory] = useState('全部詞彙'),
    [filter, setFilter] = useState('all');
  const [selection, setSelection] = useState(null),
    [active, setActive] = useState(null),
    [composer, setComposer] = useState(null),
    [saving, setSaving] = useState(false),
    [modal, setModal] = useState(null),
    [history, setHistory] = useState([]),
    [toast, setToast] = useState(''),
    [conflict, setConflict] = useState(null);
  const {edit, setEdit, draft: articleDraft} = useArticleDraft(remote?.user?.id, message => setToast(message));
  const [reading, setReading] = useState('zh'),
    [english, setEnglish] = useState(null),
    [englishLoading, setEnglishLoading] = useState(false),
    [englishError, setEnglishError] = useState('');
  const [aboutOpen, setAboutOpen] = useState(new URLSearchParams(location.search).get('view') === 'about');
  const [adminOpen, setAdminOpen] = useState(new URLSearchParams(location.search).get('view') === 'admin');
  const [conversationTab, setConversationTab] = useState(() => new URLSearchParams(location.search).get('conversation') === 'threads' ? 'threads' : 'chat');
  const [expandedMessages, setExpandedMessages] = useState(new Set());
  const panelScroll = useRef({});
  function expandMessage(id, open) {
    setExpandedMessages(previous => {
      const next = new Set(previous);
      open ? next.add(id) : next.delete(id);
      return next;
    });
  }
  useEffect(() => {
    const reveal = e => expandMessage(e.detail, true);
    window.addEventListener('wiki-reveal-message', reveal);
    return () => window.removeEventListener('wiki-reveal-message', reveal);
  }, []);
  const [splitLanguage, setSplitLanguage] = useState('en');
  const splitPositions = useRef({
      en: 0,
      zh: 0
    }),
    splitRestore = useRef(null);
  function switchSplitLanguage(language) {
    if (language === splitLanguage) return;
    splitPositions.current[splitLanguage] = articleRef.current?.scrollTop || 0;
    splitRestore.current = splitPositions.current[language] || 0;
    setSplitLanguage(language);
    clearSelection();
  }
  useLayoutEffect(() => {
    if (splitRestore.current !== null) {
      articleRef.current?.scrollTo({
        top: splitRestore.current,
        behavior: 'instant'
      });
      splitRestore.current = null;
    }
  }, [splitLanguage]);
  const mobileEnglish = reading === 'en' || reading === 'split' && splitLanguage === 'en';
  const articleRef = useRef(),
    directoryRef = useRef(),
    directoryScroll = useRef(0),
    directoryReturnTerm = useRef(null);
  const terms = remote?.terms || [],
    comments = remote?.comments || [],
    user = remote?.user;
  const canEdit = Boolean(user?.canEdit || user?.role === 'editor' || user?.role === 'admin');
  const [notifications, setNotifications] = useState([]);
  const [editSection, setEditSection] = useState(null);
  const term = terms.find(t => t.id === selectedId) || terms[0];
  const [chineseDocument, chineseDocumentRef] = useState(null);
  const [compactChinese, setCompactChinese] = useState(true);
  useLayoutEffect(() => {
    if (!chineseDocument || edit) return;
    // The article is remounted when returning from the directory or history.
    // Observe that actual prose node, rather than retaining a detached ref.
    const measure = width => {
      // Hidden language editions report zero; retain their last usable layout.
      if (width > 0) setCompactChinese(width < 600);
    };
    const style = getComputedStyle(chineseDocument);
    measure(chineseDocument.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight));
    const observer = new ResizeObserver(([entry]) => measure(entry.contentRect.width));
    observer.observe(chineseDocument);
    return () => observer.disconnect();
  }, [chineseDocument, Boolean(edit)]);
  const nameProposals=useNameProposals(selectedId?term:null,user,api);
  const polls=usePolls(selectedId?term:null,user,api);
  const currentPollScope=pollScope.termId===term?.id?pollScope.id:'names';
  const isDirectory = !selectedId || !terms.some(t => t.id === selectedId);
  const [readyEditor, setReadyEditor] = useState(null);
  const WikiEditor = readyEditor || DeferredWikiEditor;
  useEffect(() => {
    if (!canEdit || isDirectory) return;
    let current = true;
    preloadEditor().then(module => { if (current) setReadyEditor(() => module.default); }).catch(() => {});
    return () => { current = false; };
  }, [canEdit, isDirectory]);
  const [historyOrigin, setHistoryOrigin] = useState(() => window.history.state?.wikiHistoryOrigin || 'article');
  const [historyOpen, setHistoryOpen] = useState(new URLSearchParams(location.search).get('view') === 'history');
  useEffect(()=>{if(!isDirectory||aboutOpen||adminOpen||historyOpen){setDirectoryTool(null);setDirectoryArranging(false);}},[isDirectory,aboutOpen,adminOpen,historyOpen]);
  const [deletedTerms, setDeletedTerms] = useState([]),
    [manageError, setManageError] = useState(''),
    [historyView, setHistoryView] = useState('versions'),
    [historyLoading, setHistoryLoading] = useState(false),
    [historyError, setHistoryError] = useState('');
  const draftScope = user?.id;
  const savedDrafts = draftScope && selectedId ? readDrafts(draftScope, selectedId) : [];
  const articleField = term?.article ? 'article' : 'definition';
  const bodyText = term?.body ?? defaultBody(term || {}),
    bodySections = parseBody(bodyText);
  const sections = term?.contentFormat === 'wikitext-v1' ? [['chinese-article-start', '序言'], ...wikiSections(term.wikitext || '').map((s, i) => ['wiki-section-' + i, s.title])] : [['chinese-article-start', '序言'], ...(term?.definition && term?.article && term.definition !== term.article ? [['wiki-overview', '概述']] : []), ...bodySections.map((s, i) => ['body-section-' + i, s.title]), ['wiki-seealso', '參見'], ['wiki-references', '參考資料'], ['wiki-external', '外部連結'], ['wiki-data', '條目資料']];
  const termComments = useMemo(() => comments.filter(c => c.termId === term?.id), [comments, term?.id]);
  const issueLinkRevision=useMemo(()=>termComments.map(c=>[c.id,c.version,c.deletedAt,c.threadDeletedAt].join(':')).join('|'),[termComments]);
  const issueLinks=useThreadIssueLinks(selectedId?term?.id:null,api,issueLinkRevision,user?.id||null);
  const [threadManagement,setThreadManagement]=useState(null);
  const [issueManagement,setIssueManagement]=useState(null);
  useEffect(()=>{setThreadManagement(null);setIssueManagement(null);},[term?.id,user?.id]);
  const topicCount=useMemo(()=>indexDiscussion(termComments).topics.length,[termComments]);
  const roots = useMemo(() => termComments.filter(c => !c.parentId), [termComments]),
    selectedComment = termComments.find(c => c.id === active);
  const tell = msg => setToast(msg);
  async function api(path, method = 'GET', body) {
    const session = storedSession();
    return requestJSON(session ? './api/transport' : './api/' + path, session ? {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        path,
        method,
        session,
        csrf: remote?.csrf || '',
        ...(body ? {
          body
        } : {})
      })
    } : {
      method,
      headers: {
        'Content-Type': 'application/json'
      },
      ...(body ? {
        body: JSON.stringify(body)
      } : {})
    }, {requestId:body?.requestId,diagnostic:/\/ai\/(edit|cancel)$/.test(path),responseShape:responseShapeFor(path, method)});

  }
  const requestContext = useRef();
  requestContext.current = {api, userId: user?.id};
  const stateRequest = useRef(), notificationRequest = useRef(), notificationOwner = useRef();
  if (!stateRequest.current) stateRequest.current = createLatestRequest({
    load: () => requestContext.current.api('state'),
    scope: storedSession,
    apply: s => {
      setRemote(s);
      if(s.categories)setCategory(current=>current==='全部詞彙'||s.categories.items.some(c=>c.name===current)?current:'全部詞彙');
      setError('');
    },
    onError: () => setError('無法載入工作台，請確認連線後重試。')
  });
  if (!notificationRequest.current) notificationRequest.current = createLatestRequest({
    load: () => requestContext.current.api('notifications'),
    scope: () => JSON.stringify([storedSession(), requestContext.current.userId]),
    apply: setNotifications
  });
  function refresh() { return stateRequest.current.run(); }
  function refreshNotifications() {
    return user ? notificationRequest.current.run() : Promise.resolve(null);
  }
  useEffect(() => {
    if (notificationOwner.current !== user?.id) {
      notificationOwner.current = user?.id;
      notificationRequest.current.invalidate();
      setNotifications([]);
    }
    if (!user) {
      notificationRequest.current.invalidate();
      setNotifications([]);
      return;
    }
    refreshNotifications();
    const timer = setInterval(refreshNotifications, 20000);
    return () => clearInterval(timer);
  }, [user?.id, remote?.comments]);
  const activeRoot = rootFor(selectedComment, termComments);
  const activeNotice = notifications.find(n => n.rootId === activeRoot?.id);
  async function readThread(c) {
    if (!user || !c) return;
    const root = rootFor(c, comments);
    const n = notifications.find(n => n.rootId === root.id);
    try {
      await api('threads/' + root.id + '/read', 'POST', {
        seen: n?.latest || c.createdAt
      });
      await refreshNotifications();
    } catch (e) {
      tell(e.message);
    }
  }
  function openNotification(n,confirmed=false) {
    if(edit&&!confirmed){pendingNavigation.current=()=>openNotification(n,true);setLeaveRequest(n=>n+1);return;}
    chooseTerm(terms.find(t => t.id === n.termId));
    setTimeout(() => {
      const target = comments.find(c => c.id === n.firstUnread) || comments.find(c => c.id === n.rootId);
      selectComment(n.chat || target?.chat ? null : target);
      if (n.chat) api('threads/' + encodeURIComponent(n.rootId) + '/read', 'POST', {
        seen: n.latest
      }).then(refreshNotifications).catch(() => {});
      setTimeout(() => revealMessage(n.firstUnread), 80);
    }, 80);
  }
  async function toggleFollow() {
    try {
      await api('threads/' + activeRoot.id + '/follow', 'POST', {
        follow: !activeNotice?.following
      });
      await refreshNotifications();
    } catch (e) {
      tell(e.message);
    }
  }
  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, 20000);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    window.addEventListener('storage', onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('storage', onFocus);
    };
  }, []);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 4500);
    return () => clearTimeout(t);
  }, [toast]);
  useEffect(() => { workspace.restoreConversation(); }, [selectedId, draftScope]);
  useEffect(() => {
    const onPop = () => {
      if(editorExit.current && !editorExit.current())return;
      setHistoryOrigin(window.history.state?.wikiHistoryOrigin || 'article');
      workspace.restoreURL();
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  useLayoutEffect(()=>{
    const node=directoryRef.current;
    if(!isDirectory||aboutOpen||adminOpen||!node)return;
    // Restoration must finish before checking whether the return row is visible.
    // The reader's smooth-scroll CSS otherwise starts an animation here.
    node.scrollTo({top:directoryScroll.current,behavior:'instant'});
    const row=[...node.querySelectorAll('[data-directory-term]')].find(el=>el.dataset.directoryTerm===directoryReturnTerm.current&&el.getClientRects().length);
    if(row){
      const bounds=node.getBoundingClientRect(),item=row.getBoundingClientRect();
      const inset=parseFloat(getComputedStyle(node).scrollPaddingTop)||0;
      if(item.bottom<=bounds.top+inset||item.top>=bounds.bottom)row.scrollIntoView({block:'nearest',behavior:'instant'});
    }
    directoryReturnTerm.current=null;
  },[isDirectory,aboutOpen,adminOpen,Boolean(remote)]);
  useEffect(() => {
    document.title = aboutOpen ? '關於本計畫 — Wiki Chat' : adminOpen ? '成員管理 — Wiki Chat' : isDirectory ? 'AI 詞彙表 — Wiki Chat' : `${term?.label || '條目'} — Wiki Chat`;
  }, [aboutOpen, adminOpen, isDirectory, selectedId, term?.label, remote?.terms?.length]);
  useEffect(() => {
    setConversationTab(new URLSearchParams(location.search).get('conversation') === 'threads' ? 'threads' : 'chat');
    setSplitLanguage('en');
    splitPositions.current = {
      en: 0,
      zh: 0
    };
    setReading('zh');
    setEnglish(null);
    setEnglishError('');
  }, [selectedId]);
  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    setEnglishLoading(true);
    api('terms/' + encodeURIComponent(selectedId) + '/english').then(value => {
      if (!cancelled) setEnglish(value);
    }).catch(e => {
      if (!cancelled) setEnglishError(e.message);
    }).finally(() => {
      if (!cancelled) setEnglishLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);
  async function openReviewDiscussion(id,knownState=null) {
    const state = knownState || await refresh();
    if(!state){tell('無法載入討論，請稍後重試。');return false;}
    const c = state?.comments.find(c => c.id === id);
    if (!c || c.deletedAt) {
      tell('這則回饋已被刪除，評價紀錄仍保留。');
      return false;
    }
    setHistoryOpen(false);
    setAboutOpen(false);
    setAdminOpen(false);
    selectComment(c);
    const u = new URL(location.href);
    u.searchParams.delete('view');
    u.searchParams.delete('oldid');
    u.searchParams.delete('diff');
    u.searchParams.set('thread', id);
    window.history.replaceState({}, '', u);
    setTimeout(()=>revealMessage(c.id),80);
    return true;
  }
  function pageThread() {
    startThread({
      field: 'page',
      version: term.version,
      quote: term.label
    });
  }
  async function showDeleted() {
    ensureEditor(async () => {
      setManageError('');
      setDeletedTerms([]);
      setModal('deleted-terms');
      try {
        setDeletedTerms(await api('terms/deleted'));
      } catch (e) {
        setManageError(e.message);
      }
    });
  }
  async function createTerm(e) {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setManageError('');
    try {
      const next = await api('terms', 'POST', Object.fromEntries(new FormData(e.currentTarget)));
      await refresh();
      setModal(null);
      setQuery('');
      setCategory('全部詞彙');
      chooseTerm(next);
      tell('詞彙已新增。');
    } catch (e) {
      setManageError(e.message);
    } finally {
      setSaving(false);
    }
  }
  async function deleteTerm() {
    if (saving) return;
    setSaving(true);
    setManageError('');
    try {
      await api('terms/' + term.id, 'DELETE', {
        version: term.version
      });
      setEdit(null);
      setModal(null);
      setSelectedId(null);
      const u = new URL(location.href);
      u.search = '';
      u.hash = '';
      historyPush(u);
      await refresh();
      tell('詞彙已刪除，可從「已刪除」復原。');
    } catch (e) {
      setManageError(e.message);
    } finally {
      setSaving(false);
    }
  }
  async function restoreTerm(t) {
    setSaving(true);
    setManageError('');
    try {
      await api('terms/' + t.id + '/restore', 'POST', {
        version: t.version
      });
      await refresh();
      setDeletedTerms(await api('terms/deleted'));
      tell('詞彙與討論已復原。');
    } catch (e) {
      setManageError(e.message);
    } finally {
      setSaving(false);
    }
  }
  function goHome(confirmed=false) {
    if(edit && confirmed!==true){pendingNavigation.current=()=>goHome(true);setLeaveRequest(n=>n+1);return;}
    workspace.navigate({type:'home'});
  }
  function chooseTerm(t,confirmed=false,focusOnArrival=false) {
    if (edit && confirmed!==true) {pendingNavigation.current=()=>chooseTerm(t,true,focusOnArrival);setLeaveRequest(n=>n+1);return;}
    if (directoryRef.current) {
      directoryScroll.current = directoryRef.current.scrollTop;
      directoryReturnTerm.current=t.id;
    }
    if(focusOnArrival)termArrival.current=t.id;
    workspace.navigate({type:'term',term:t});
  }
  useLayoutEffect(()=>{
    if(edit||!termArrival.current||termArrival.current!==selectedId)return;
    const title=articleRef.current?.querySelector('.workspace-title h1');
    if(!title)return;
    // Let the reader's language/panel restoration settle before handing focus
    // to the destination. In particular, mobile scroll anchoring can otherwise
    // preserve part of the old article's position across the changed title.
    const frame=requestAnimationFrame(()=>{
      articleRef.current?.scrollTo({top:0,behavior:'instant'});
      title.focus({preventScroll:true});
      termArrival.current=null;
    });
    return()=>cancelAnimationFrame(frame);
  },[selectedId,Boolean(edit)]);
  function openAbout(confirmed=false) {
    if(edit && confirmed!==true){pendingNavigation.current=()=>openAbout(true);setLeaveRequest(n=>n+1);return;}
    if (directoryRef.current) directoryScroll.current = directoryRef.current.scrollTop;
    workspace.navigate({type:'about'});
  }
  function historyPush(u) {
    window.history.pushState({}, '', u);
  }
  function ensureEditor(action) {
    if (!user) {
      setModal('login');
      return;
    }
    if (!canEdit) {
      tell('正文由共編者整理，你可以反白文字提出討論。');
      return;
    }
    action();
  }
  function ensureWrite(action) {
    if (!user) {
      setModal('login');
      return false;
    }
    action();
    return true;
  }
  useEffect(() => {
    const menus = '.mobile-tools[open],.mobile-toc[open],.notifications[open],.message-menu[open],.outline-item>details[open],.wiki-link-choices[open],.editor-mobile-insert[open]';
    const outside = e => document.querySelectorAll(menus).forEach(menu => {
      if (!menu.contains(e.target)) menu.open = false;
    });
    const escape = e => {
      if (e.key !== 'Escape') return;
      const menu = [...document.querySelectorAll(menus)].find(m => m.contains(document.activeElement));
      document.querySelectorAll(menus).forEach(m => m.open = false);
      menu?.querySelector('summary')?.focus();
    };
    document.addEventListener('pointerdown', outside, true);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside, true);
      document.removeEventListener('keydown', escape);
    };
  }, []);
  useLayoutEffect(() => {
    const nav = document.querySelector('.wiki-navigation');
    if (!nav) return;
    const update = () => document.documentElement.style.setProperty('--navigation-height', nav.getBoundingClientRect().height + 'px');
    update();
    const observer = new ResizeObserver(update);
    observer.observe(nav);
    return () => observer.disconnect();
  }, [isDirectory, historyOpen, Boolean(edit)]);
  useEffect(()=>{
    setCompactTitle(false);
    if(isDirectory||historyOpen||edit)return;
    const root=articleRef.current,title=root?.querySelector('.workspace-title');if(!root||!title)return;
    const observer=new IntersectionObserver(([entry])=>setCompactTitle(!entry.isIntersecting&&entry.boundingClientRect.top<(entry.rootBounds?.top??0)),{root,threshold:0});
    observer.observe(title);return()=>observer.disconnect();
  },[selectedId,isDirectory,historyOpen,Boolean(edit)]);
  function rememberEditorFocus(e){
    const node=e.target;if(!edit||!node.closest('.integrated-editor')||!node.matches('input,textarea,[contenteditable]'))return;
    const selection=window.getSelection();editorFocus.current={node,start:node.selectionStart,end:node.selectionEnd,range:node.isContentEditable&&selection?.rangeCount?selection.getRangeAt(0).cloneRange():null};
  }
  function discussionHint() { return <div className="wiki-notice"><TextSelect size={17} /><span>反白文字開啟討論。<button onClick={() => setDiscussionOpen(true)}>查看討論</button></span></div>; }
  function clearSelection() {
    setSelection(null);
    setSelectionPoint(null);
    window.getSelection()?.removeAllRanges();
  }
  useEffect(() => {
    if (!selection) return;
    const dismiss = e => {
      if (e.target instanceof Element && e.target.closest('.selection-bar')) return;
      const current = window.getSelection();
      if (current?.rangeCount && !current.isCollapsed) {
        const rects = [...current.getRangeAt(0).getClientRects()];
        if (rects.some(r => e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom)) return;
      }
      clearSelection();
    };
    const escape = e => {
      if (e.key === 'Escape') clearSelection();
    };
    document.addEventListener('pointerdown', dismiss, true);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', dismiss, true);
      document.removeEventListener('keydown', escape);
    };
  }, [selection]);
  const selectionToolbarRef = useRef(null);
  useLayoutEffect(() => {
    if (!selection || edit) return;
    const place = () => {
      const toolbar = selectionToolbarRef.current,
        range = window.getSelection();
      if (!toolbar || !range?.rangeCount) return;
      const anchor = range.getRangeAt(0).getBoundingClientRect(),
        box = toolbar.getBoundingClientRect();
      const viewport = window.visualViewport,
        left = viewport?.offsetLeft || 0,
        top = viewport?.offsetTop || 0,
        width = viewport?.width || innerWidth,
        height = viewport?.height || innerHeight,
        pad = 12;
      const below = anchor.bottom + 10,
        preferred = below + box.height <= top + height - pad ? below : anchor.top - box.height - 10;
      setSelectionPoint({
        left: Math.max(left + pad, Math.min(anchor.left, left + width - box.width - pad)),
        top: Math.max(top + pad, Math.min(preferred, top + height - box.height - pad))
      });
    };
    place();
    window.addEventListener('resize', place);
    window.visualViewport?.addEventListener('resize', place);
    return () => {
      window.removeEventListener('resize', place);
      window.visualViewport?.removeEventListener('resize', place);
    };
  }, [selection, edit]);
  function captureSelection() {
    if (edit) return;
    const s = window.getSelection();
    if (!s || s.isCollapsed || !s.rangeCount) {
      setSelection(null);
      setSelectionPoint(null);
      return;
    }
    const range = s.getRangeAt(0),
      node = range.startContainer.nodeType === 1 ? range.startContainer : range.startContainer.parentElement;
    const el = node.closest('[data-field]');
    if (!el || !(articleRef.current?.contains(el) || document.querySelector('.workspace-title')?.contains(el)) || !el.contains(range.endContainer)) return;
    const prefix = range.cloneRange();
    prefix.selectNodeContents(el);
    prefix.setEnd(range.startContainer, range.startOffset);
    let start = Number(el.dataset.offset || 0) + prefix.toString().length,
      end = start + range.toString().length;
    if (el.dataset.wikitext) {
      const offset = (container, position) => {
        const node = container.nodeType === 3 ? container.parentElement : container,
          leaf = node.closest('[data-wiki-start]');
        if (!leaf) return null;
        const r = document.createRange();
        r.selectNodeContents(leaf);
        r.setEnd(container, position);
        return Number(leaf.dataset.wikiStart) + r.toString().length;
      };
      start = offset(range.startContainer, range.startOffset);
      end = offset(range.endContainer, range.endOffset);
      if (start === null || end === null || end <= start) return;
    }
    if (!range.toString().trim()) return;
    const isEnglish = el.dataset.field === 'english';
    const edition = isEnglish ? el.dataset.edition : undefined;
    const anchor = {
      ...buildAnchor(isEnglish ? (edition === 'structured-v1' ? english?.richText : english?.text) || '' : term[el.dataset.field], start, end, el.dataset.field, isEnglish ? english?.revision : term.version),
      ...(edition ? {
        edition
      } : {})
    };
    if (el.dataset.wikitext) {
      anchor.sourceQuote = anchor.quote;
      anchor.quote = wikiVisibleRange(term.wikitext, start, end);
    }
    const rect = range.getBoundingClientRect();
    setSelectionPoint({
      left: Math.max(12, Math.min(rect.left, window.innerWidth - 310)),
      top: Math.max(80, Math.min(rect.bottom + 10, window.innerHeight - 65))
    });
    setSelection(anchor);
  }
  function openComposer(next) { workspace.compose(next); }
  function startThread(anchor) {
    if(!user){workspace.requireLogin('opinion',{anchor});clearSelection();return;}
    workspace.compose({type:'new',anchor});clearSelection();
  }
  function selectComment(c, tab = c ? 'threads' : 'chat') {
    readThread(c);workspace.conversation(c,tab);
  }
  function focusThread(c) {
    selectComment(c);
    setTimeout(() => revealMessage(c.id), 60);
  }
  function gotoQuote(c) {
    if(edit){showPane('article');tell('已返回編輯中的草稿，原文引用保留在討論中。');return;}
    if (!c.anchor) return;
    const status=quoteLocation(term,c.anchor,english);
    if(status){tell(status);return;}
    setActive(c.id);
    setReading(c.anchor.field === 'english' ? 'en' : 'zh');
    showPane('article');
    setTimeout(() => {
      const name = c.anchor.field === 'definition' && term.definition === term.article ? 'article' : c.anchor.field;
      const scope = name === 'label' ? document.querySelector('.workspace-title') : articleRef.current;
      const el = scope?.querySelector(`[data-comment-id="${c.id}"]`);
      el?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      if(!el){tell('找不到引用位置，原引文仍保留在討論中。');return;}
      if(!el.hasAttribute('tabindex'))el.setAttribute('tabindex','-1');el.focus({preventScroll:true});
      el?.classList.add('flash');
      setTimeout(() => el?.classList.remove('flash'), 1500);
    }, 50);
  }
  function gotoChinese(c) {
    if(edit){showPane('article');return;}
    setReading('zh');
    showPane('article');
    const index = bodySections.findIndex(s => s.title === c.anchor?.targetSection);
    setTimeout(() => jumpSection(index >= 0 ? 'body-section-' + index : 'wiki-top'), 80);
    if (c.anchor?.targetSection && c.anchor.targetSection !== '序言' && index < 0) tell('原連結章節已改名或移除，原章節名稱仍保留在討論中。');
  }
  const [saveHost,setSaveHost]=useState(null);
  const [aiHost,setAIHost]=useState(null);
  useEffect(()=>{if(!edit&&conversationTab==='ai')setConversationTab('chat');},[Boolean(edit),conversationTab]);
  function openEditorAI(){selectComment(null,'ai');showPane('discussion');requestAnimationFrame(()=>document.getElementById('chat-tab-ai')?.focus({preventScroll:true}));}
  const rightOpen=discussionOpen&&(screenWidth>850||mobilePane==='discussion');
  const threadDetailOpen=rightOpen&&conversationTab!=='ai'&&Boolean(active||composer?.type==='new');
  const namesVisible=namesOpen&&(screenWidth>850||mobilePane==='names');
  const articleVisible=screenWidth>850||mobilePane==='article';
  const currentPane=mobilePane;
  const pointerFocusTarget=useRef(null);
  useEffect(()=>{
    let release;
    const press=event=>{clearTimeout(release);pointerFocusTarget.current=event.target;};
    const releasePointer=()=>{release=setTimeout(()=>{pointerFocusTarget.current=null;},0);};
    const keyboard=()=>{pointerFocusTarget.current=null;};
    window.addEventListener('pointerdown',press,true);
    window.addEventListener('pointerup',releasePointer,true);
    window.addEventListener('pointercancel',releasePointer,true);
    window.addEventListener('keydown',keyboard,true);
    return()=>{
      clearTimeout(release);
      window.removeEventListener('pointerdown',press,true);
      window.removeEventListener('pointerup',releasePointer,true);
      window.removeEventListener('pointercancel',releasePointer,true);
      window.removeEventListener('keydown',keyboard,true);
    };
  },[]);
  function revealWorkspacePane(node){
    if(screenWidth<=850||!node)return;
    const viewport=workspaceViewport.current,pane=node.closest('.translation-panel,.wiki-article-pane,.discussion-detail-pane,.article-discussions');
    if(!viewport||!pane)return;
    const bounds=viewport.getBoundingClientRect();
    let target=pane.getBoundingClientRect();
    // Keep the thread and its index together whenever that pair fits.
    if(threadDetailOpen&&pane.matches('.discussion-detail-pane,.article-discussions')){
      const detail=discussionDetailHost?.getBoundingClientRect(),index=viewport.querySelector('.article-discussions')?.getBoundingClientRect();
      if(detail&&index&&index.right-detail.left<=bounds.width)target={left:detail.left,right:index.right};
    }
    if(target.left<bounds.left)viewport.scrollLeft+=target.left-bounds.left;
    else if(target.right>bounds.right)viewport.scrollLeft+=target.right-bounds.right;
  }
  function focusWorkspacePane(event){
    // Moving the pressed control before pointerup cancels native click/details.
    // Keyboard and explicit focus handoffs still reveal the destination pane.
    if(!pointerFocusTarget.current||!event.target.contains(pointerFocusTarget.current))revealWorkspacePane(event.target);
    if(screenWidth<=850)return;
    const pane=event.target.closest('.translation-panel,.wiki-article-pane,.discussion-detail-pane,.article-discussions');
    if(pane)setMobilePane(pane.classList.contains('translation-panel')?'names':pane.classList.contains('wiki-article-pane')?'article':'discussion');
  }
  useLayoutEffect(()=>{if(namesVisible)revealWorkspacePane(namesRef.current);},[namesVisible]);
  useLayoutEffect(()=>{if(threadDetailOpen)revealWorkspacePane(discussionDetailHost);},[threadDetailOpen]);
  useEffect(()=>{setVoteSource(null);},[term?.id,user?.id]);
  useEffect(()=>{if(!term)return;let live=true;api('discussion-vote-sources?term='+encodeURIComponent(term.id)).then(result=>{if(live)setDiscussionVoteSources(result.items||[]);}).catch(()=>{if(live)setDiscussionVoteSources([]);});return()=>{live=false;};},[term?.id,termComments]);
  function openLinkedIssue(link){
    if(link.issue.status==='deleted'){if(user?.role==='admin')setIssueManagement({mode:'deleted'});else tell('此議題已刪除，原討論串仍保留。');return;}
    if(link.issue.kind==='names')changePollScope('names');else changePollScope(link.issue.pollId);
    showPane('names');requestAnimationFrame(()=>pollHeadingRef.current?.focus({preventScroll:true}));
  }
  function openLinkedThread(link){
    const source=termComments.find(comment=>comment.id===link.rootId);
    if(source)openSourceDiscussion({sourceRootId:source.id,sourceCommentId:source.id});
    else if(user?.role==='admin')setThreadManagement({mode:'deleted'});
    else tell('此討論串已刪除。');
  }
  function viewDiscussionVote(result){
    const target=result.target||result.link?.target||result.source?.target;
    if(!target)return;
    setVoteSource(null);
    if(target.kind==='names'){changePollScope('names');setNameReveal(old=>({id:target.proposalId,termId:term.id,sequence:(old?.sequence||0)+1}));}
    else{changePollScope(target.pollId);setPollReveal({...target,sequence:Date.now()});}
    showPane('names');
    if(!target.optionId&&!target.proposalId)requestAnimationFrame(()=>pollHeadingRef.current?.focus({preventScroll:true}));
    polls.reload();nameProposals.reload();
  }
  const workspace=useArticleWorkspace({
    context:()=>({user,term,selectedId,edit,modal,historyOpen,english,englishLoading,screenWidth,
      pendingOpinion,pendingName,pendingPoll,nameProposeRequest,pollResume,conversationTab,discussionOpen,namesOpen,mobilePane,
      namesVisible,articleVisible,currentPollScope,tell,namesScroll:()=>namesRef.current?.scrollTop||0}),
    update:patch=>{
      const setters={pendingOpinion:setPendingOpinion,pendingName:setPendingName,pendingPoll:setPendingPoll,
        modal:setModal,aboutOpen:setAboutOpen,adminOpen:setAdminOpen,historyOpen:setHistoryOpen,selectedId:setSelectedId,
        namesOpen:setNamesOpen,discussionOpen:setDiscussionOpen,mobilePane:setMobilePane,selection:setSelection,conversationTab:setConversationTab,
        active:setActive,composer:setComposer,pollScope:setPollScope,nameProposeRequest:setNameProposeRequest,pollResume:setPollResume};
      for(const [key,value] of Object.entries(patch))setters[key](value);
    },
    readDrafts,
    scroll:(target,top)=>{if(target==='window')window.scrollTo({top,behavior:'instant'});else (target==='article'?articleRef:namesRef).current?.scrollTo({top,behavior:'instant'});},
    focus:target=>{
      if(target==='clear-selection'){clearSelection();return;}
      if(target==='editor'){const saved=editorFocus.current;if(saved?.node.isConnected){saved.node.focus({preventScroll:true});if(saved.start!=null&&saved.node.setSelectionRange)saved.node.setSelectionRange(saved.start,saved.end);else if(saved.range){const selection=window.getSelection();selection.removeAllRanges();selection.addRange(saved.range);}}return;}
      const targets={
        'article-tab':()=>articleTabRef.current,'poll-heading':()=>pollHeadingRef.current,
        'poll-content':()=>namesRef.current?.querySelector('button,select'),
        'poll-return':()=>{const trigger=pollReturnRef.current;return trigger?.isConnected&&trigger.getClientRects().length?trigger:pollToggleRef.current;},
        'discussion-toggle':()=>discussionToggleRef.current,'ai-toggle':()=>document.querySelector('.editor-ai-toggle'),
        'discussion-tab':()=>document.querySelector('#article-discussion-panel [role="tab"][aria-selected="true"]'),
        composer:()=>document.querySelector('#discussion-detail-panel #chat-input')||document.querySelector('.right-chat-content #chat-input'),
        'name-propose':()=>namesRef.current?.querySelector('.name-propose-toggle')
      };targets[target]?.()?.focus({preventScroll:true});
    }
  });
  useEffect(()=>{workspace.resume();},[user?.id,pendingOpinion,pendingName,pendingPoll,modal,Boolean(edit),historyOpen,term?.id,term?.version,english?.revision,englishLoading]);
  const previewNames=[...nameProposals.items].filter(p=>!p.current).sort((a,b)=>a.createdAt.localeCompare(b.createdAt)||a.name.localeCompare(b.name)).slice(0,6);
  function openNameDiscussion(p){
    setNameDiscussionFocus(old=>({id:p.id,termId:term.id,sequence:(old?.sequence||0)+1}));
    if(p.commentId)selectComment(termComments.find(c=>c.id===p.commentId)||{id:p.commentId,termId:term.id});else selectComment(null,'names');
  }
  function returnToNames(p){openNames(p);requestAnimationFrame(()=>pollHeadingRef.current?.focus({preventScroll:true}));}
  function openNames(proposal){pollReturnRef.current=document.activeElement;changePollScope('names');showPane('names');if(!proposal)setPollFocusRequest({target:'heading'});if(proposal)setNameReveal(old=>({id:proposal.id,termId:term.id,sequence:(old?.sequence||0)+1}));}
  function changePollScope(id){workspace.pollScope(id);}
  function openPolls(id=null){changePollScope(id);showPane('names');}
  useLayoutEffect(()=>{workspace.restoreScope();},[term?.id,currentPollScope]);
  function openPollDiscussion(p,o){const id=o?.commentId||p.commentId;const url=new URL(location.href);url.searchParams.set('thread',id);window.history.replaceState(window.history.state,'',url);setPollFocus({commentId:id,sequence:Date.now()});selectComment(termComments.find(c=>c.id===id)||{id,termId:term.id});}
  function returnToPoll(context){setPollReveal({...context,sequence:Date.now()});openPolls(context.pollId);requestAnimationFrame(()=>pollHeadingRef.current?.focus({preventScroll:true}));}
  function openSourceDiscussion(source){const comment=termComments.find(c=>c.id===source.sourceCommentId)||termComments.find(c=>c.id===source.sourceRootId);if(comment)selectComment(comment);}
  function closeDiscussionPanel(){workspace.close('discussion');}
  function toggleDiscussionPanel(){workspace.toggle('discussion');}
  function closePollPanel(){workspace.close('names');}
  function togglePollPanel(){pollReturnRef.current=pollToggleRef.current;workspace.toggle('names');}
  useLayoutEffect(()=>{
    if(!pollFocusRequest)return;
    if(pollFocusRequest.target==='heading'&&namesVisible)pollHeadingRef.current?.focus({preventScroll:true});
    if(pollFocusRequest.target==='return'&&!namesVisible){const trigger=pollReturnRef.current;const destination=screenWidth<=850?articleTabRef.current:trigger?.isConnected&&trigger.getClientRects().length?trigger:pollToggleRef.current;destination?.focus({preventScroll:true});}
    setPollFocusRequest(null);
  },[pollFocusRequest,namesVisible,screenWidth]);
  function showPane(pane){workspace.pane(pane);}
  function returnToArticle(){
    showPane('article');
    requestAnimationFrame(()=>{
      if(!edit)articleRef.current?.querySelector('.workspace-title h1')?.focus({preventScroll:true});
      revealWorkspacePane(articleRef.current);
    });
  }
  useLayoutEffect(()=>{workspace.resetTerm();},[term?.id]);
  useLayoutEffect(()=>{workspace.restorePanes();},[articleVisible,namesVisible]);
  useEffect(()=>{
    const save=()=>{if(screenWidth<=850&&articleVisible)workspace.saveScroll('article',window.scrollY);};
    window.addEventListener('scroll',save,{passive:true});return()=>window.removeEventListener('scroll',save);
  },[screenWidth,articleVisible]);
  function jumpSection(section) {
    if (section === 'wiki-top') {
      articleRef.current?.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      if (window.innerWidth <= 850) window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }
    const el = articleRef.current?.querySelector('#' + CSS.escape(section));
    const details = el?.closest('details');
    if (details) details.open = true;
    el?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }
  const dictionary = useMemo(() => terms.filter(t => t.id !== term?.id).flatMap(t => termNames(t).filter(w => w.length > 1 && resolveNames(terms, w).length === 1).map(word => ({
    word,
    t
  }))).sort((a, b) => b.word.length - a.word.length), [terms, term?.id]);
  const own = useMemo(() => [term?.label, ...(term?.aliases || '').split('\n')].filter(Boolean).sort((a, b) => b.length - a.length), [term?.label, term?.aliases]);
  function linkedText(text, keyPrefix = 'text') {
    const output = [];
    let pos = 0,
      plain = '';
    while (pos < text.length) {
      const ownWord = own.find(w => text.startsWith(w, pos));
      if (ownWord) {
        plain += ownWord;
        pos += ownWord.length;
        continue;
      }
      const hit = dictionary.find(({
        word
      }) => text.startsWith(word, pos) && (!/^[a-z0-9]/i.test(word) || !/[a-z0-9]/i.test(text[pos - 1] || '') && !/[a-z0-9]/i.test(text[pos + word.length] || '')));
      if (hit) {
        if (plain) {
          output.push(plain);
          plain = '';
        }
        output.push(<a className="wiki-inline-link" key={keyPrefix + pos} href={'?term=' + encodeURIComponent(hit.t.id)} title={'閱讀「' + hit.t.label + '」條目'} onClick={e => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          if (window.getSelection()?.toString()) return;
          chooseTerm(hit.t);
        }}>{hit.word}</a>);
        pos += hit.word.length;
      } else {
        plain += text[pos];
        pos++;
      }
    }
    if (plain) output.push(plain);
    return output;
  }
  function annotated(text, field, offset = 0) {
    if (!text) return null;
    const ranges = roots.filter(c => c.anchor?.field === field || field === 'article' && term.definition === term.article && c.anchor?.field === 'definition').map(c => ({
      c,
      pos: locateAnchor(term[field] || '', c.anchor)
    })).filter(a => a.pos && a.pos.start >= offset && a.pos.end <= offset + text.length).map(a => ({
      ...a,
      pos: {
        start: a.pos.start - offset,
        end: a.pos.end - offset
      }
    })).sort((a, b) => a.pos.start - b.pos.start);
    const output = [];
    let cursor = 0;
    for (const {
      c,
      pos
    } of ranges) {
      if (pos.start < cursor) continue;
      output.push(...linkedText(text.slice(cursor, pos.start), 'part' + cursor));
      output.push(<mark data-comment-id={c.id} key={c.id} className={activeRoot?.id === c.id ? 'active' : ''} tabIndex={0} role="button" title="開啟這段文字的討論" onClick={() => focusThread(c)} onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          focusThread(c);
        }
      }}>{text.slice(pos.start, pos.end)}</mark>);
      cursor = pos.end;
    }
    output.push(...linkedText(text.slice(cursor), 'last' + cursor));
    return output;
  }
  function field(text, name, className = '', offset = 0) {
    return <div data-field={name} data-offset={offset} className={'selectable ' + className}>{annotated(text, name, offset)}</div>;
  }
  async function deleteComment(c) {
    if (!user?.canDeleteComments || saving) return;
    if (!c.deletedAt && !window.confirm('刪除這則留言？內容將隱藏，回覆與刪除紀錄會保留。')) return;
    setSaving(true);
    try {
      await api('comments/' + c.id + (c.deletedAt ? '/restore' : ''), c.deletedAt ? 'POST' : 'DELETE', {
        version: c.version
      });
      if (composer?.parentId === c.id) {
        setComposer(null);
      }
      await refresh();
      tell(c.deletedAt ? '留言已還原。' : '留言已刪除，回覆與紀錄已保留。');
    } catch (e) {
      tell(e.message);
    } finally {
      setSaving(false);
    }
  }
  function beginEdit(section = null,discussion=null) {
    if(edit){if(discussion)setEdit(current=>({...current,discussionIds:[...new Set([...(current.discussionIds||[]),discussion.id])],contextDiscussionId:discussion.id}));showPane('article');return;}
    setEditError('');setSavedEdit(null);
    ensureEditor(() => {
      showPane('article');
      if (!remote.capabilities?.wikitext) {
        tell('新版原始碼編輯器尚待伺服器更新，草稿不會送到舊版服務。');
        return;
      }
      setReading('zh');
      const opened = articleDraft.begin(term, {categories: remote.categories?.items, section, discussion});
      setEditSection(opened.section);
      if (opened.restored) tell('已恢復草稿。');
      setConflict(null);
      setSelection(null);
    });
  }
  function leaveEditor(discard=false) {
    if(saving)return false;
    try { if (!articleDraft.leave(discard)) return false; }
    catch {setEditError('無法保存草稿，請先複製內容再離開。');return false;}
    setEdit(null);setConflict(null);setEditError('');
    if(discard!=='clean')tell(discard?'已丟棄草稿。':'草稿已保留，可稍後繼續。');
    const next=pendingNavigation.current;pendingNavigation.current=null;next?.();return true;
  }
  useLayoutEffect(()=>{
    const originalURL=location.href,originalState=window.history.state;
    editorExit.current=edit?()=>{pendingNavigation.current=null;if(leaveEditor(false))return true;window.history.pushState(originalState,'',originalURL);return false;}:null;
  });
  useLayoutEffect(()=>{
    if(edit||!returnLocation.current)return;
    const index=returnLocation.current.section;returnLocation.current=null;
    const el=document.getElementById('wiki-section-'+index)||document.querySelector('.wiki-document');
    el?.scrollIntoView({block:'start',behavior:'instant'});
    if(el){el.setAttribute('tabindex','-1');el.focus({preventScroll:true});}
  },[edit]);
  async function saveEdit(e,position={section:-1}) {
    e.preventDefault();
    if (saving || conflict) return;
    setSaving(true);setEditError('');
    try {
      const outcome = await articleDraft.save(api, refresh);
      if (!outcome) return;
      const {saved, previous, discussionIds, warning, refreshedState:refreshed} = outcome;
      if(!refreshed)setRemote(current=>current?{...current,terms:current.terms.map(t=>t.id===saved.id?{...t,...saved,versionCount:(t.versionCount||1)+1,reviewSummary:{accept:0,great:0,reject:0,total:0}}:t)}:current);
      returnLocation.current=position;
      setSavedEdit({term:saved,previous,hasDiscussions:Boolean(discussionIds.length||refreshed?.comments?.some(c=>c.termId===saved.id&&c.quoteChange?.version===saved.version))});
      setEdit(null);
      setConflict(null);
      setToast(warning);
    } catch (e) {
      if (e.current) { setConflict(e.current); setToast(''); }
      else setEditError(e.message||'儲存失敗，請重試。');
    } finally {
      setSaving(false);
    }
  }
  function closeHistory() {
    setHistoryOpen(false);
    const u = new URL(location.href);
    for (const k of ['view', 'oldid', 'diff']) u.searchParams.delete(k);
    if (historyOrigin === 'directory') {
      setSelectedId(null);
      u.searchParams.delete('term');
      u.searchParams.delete('thread');
      u.hash = '';
    }
    historyPush(u);
  }
  function showHistory(target = term, revision = null, previous = null) {
    if (edit) {
      tell('請先儲存或關閉編輯器。');
      return;
    }
    setAboutOpen(false);
    const origin = isDirectory ? 'directory' : 'article';
    setHistoryOrigin(origin);
    if (directoryRef.current) {
      directoryScroll.current = directoryRef.current.scrollTop;
      directoryReturnTerm.current=target.id||term.id;
    }
    setSelectedId(target.id || term.id);
    setDiscussionOpen(false);
    setHistoryView('versions');
    setHistoryOpen(true);
    const u = new URL(location.href);
    u.searchParams.set('term', target.id || term.id);
    u.searchParams.delete('conversation');
    u.searchParams.delete('thread');
    u.searchParams.delete('oldid');
    u.searchParams.delete('diff');
    u.searchParams.set('view', 'history');
    if (revision) { u.searchParams.set('oldid', previous ?? Math.max(1, revision - 1)); u.searchParams.set('diff', revision); }
    window.history.pushState({
      wikiHistoryOrigin: origin
    }, '', u);
  }
  useEffect(() => {
    if (!historyOpen || !selectedId) return;
    let cancelled = false;
    setHistory([]);
    setHistoryError('');
    setHistoryLoading(true);
    api('terms/' + selectedId + '/history?revisions=all').then(data => {
      if (!cancelled) setHistory(data);
    }).catch(e => {
      if (!cancelled) setHistoryError(e.message);
    }).finally(() => {
      if (!cancelled) setHistoryLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [historyOpen, selectedId]);
  async function restoreVersion(snapshot) {
    if (edit) throw new Error('請先儲存或取消目前的編輯，再還原版本。');
    await api('terms/' + term.id, 'PATCH', {
      ...versionFields(snapshot),
      version: term.version,
      restoreVersion: snapshot.version
    });
    await refresh();
    setHistory(await api('terms/' + term.id + '/history?revisions=all'));
    tell('已建立還原版本。');
  }
  async function setResolution(c, resolved, conclusion = '', resolutionVersion = null) {
    setSaving(true);
    try {
      await api('comments/' + c.id, 'PATCH', {
        version: c.version,
        resolved,
        conclusion,
        ...(resolved ? {resolutionVersion} : {})
      });
      await refresh();
      setModal(null);
      tell(resolved ? '已記錄暫定結論。' : '已重新開啟討論。');
    } catch (e) {
      tell(e.message);
    } finally {
      setSaving(false);
    }
  }
  async function copyLink() {
    const url = new URL(location.href);
    url.searchParams.set('term', term.id);
    url.searchParams.delete('thread');
    try {
      await navigator.clipboard.writeText(url.href);
      tell('已複製連結。');
    } catch {
      tell('無法存取剪貼簿，請複製瀏覽器網址。');
    }
  }
  useEffect(() => {
    if (!remote) return;
    const thread = new URLSearchParams(location.search).get('thread');
    if (thread && comments.some(c => c.id === thread)) {
      setActive(thread);setConversationTab('threads');
      setDiscussionOpen(true);
      setTimeout(() => revealMessage(thread), 100);
    }
  }, [remote?.terms?.length]);
  const categories=['全部詞彙',...(remote?.categories?activeCategories(remote.categories).filter(categoryAssignable).map(c=>c.name):[...new Set([...initialCategoryNames,...terms.map(t=>t.category)])])];
  const filtered = terms.filter(t => (category === '全部詞彙' || categoryMatches(t,category)) && normalizeName(`${t.english} ${t.label} ${t.aliases} ${t.redirects || ''} ${t.article} ${t.definition}`).includes(normalizeName(query)) && (filter === 'all' || (filter === 'open' ? comments.some(c => c.termId === t.id && !c.parentId && !c.resolved) : t.status === '團隊暫定')));
  const neighbors=adjacentTerms(terms,remote?.categories,term);
  if (!remote) return <div className="loading-screen"><div className="brand-mark"><WikiChatMark size={25} /></div><h1>Wiki Chat</h1>{error ? <><p>{error}</p><button className="primary" onClick={refresh}>重新載入</button></> : <><LoaderCircle className="spin" size={22} /><p>正在打開詞彙工作台…</p></>}</div>;
  const directoryToolsVisible=isDirectory&&!aboutOpen&&!adminOpen&&!historyOpen;
  return <div className={'app wiki-app ' + (isDirectory ? 'directory-route' : 'article-route') + (edit ? ' is-editing' : '')+(directoryToolsVisible?' has-directory-tools':'')}>
    <header className="global-header"><a className="brand" aria-label="Wiki Chat 首頁" href="./" onClick={e => {
        e.preventDefault();
        goHome();
      }}><span className="brand-mark"><WikiChatMark size={22} /></span><strong>Wiki Chat</strong></a>{!isDirectory&&!aboutOpen&&!adminOpen&&!historyOpen&&<a className="article-home-link" href="./" onClick={e=>{if(e.metaKey||e.ctrlKey||e.shiftKey||e.altKey)return;e.preventDefault();goHome();}}><ArrowLeft size={16} aria-hidden="true"/><span>返回總表</span></a>}{directoryToolsVisible&&<div className="directory-tools-actions" role="group" aria-label="查找詞彙"><button ref={directorySearchTrigger} type="button" className="directory-tool-trigger" aria-label="搜尋詞彙" title="搜尋詞彙" aria-expanded={directoryTool==='search'} aria-controls="directory-tools-panel" data-active={Boolean(query)} disabled={directoryArranging} onClick={()=>setDirectoryTool(current=>current==='search'?null:'search')}><Search size={20} aria-hidden="true"/>{query&&<span className="directory-tool-dot" aria-hidden="true"/>}</button><button ref={directoryCategoryTrigger} type="button" className="directory-tool-trigger" aria-label={'篩選分類：'+category} title="篩選分類" aria-expanded={directoryTool==='category'} aria-controls="directory-tools-panel" aria-haspopup="listbox" data-active={category!=='全部詞彙'} disabled={directoryArranging} onKeyDown={e=>{if(['ArrowDown','ArrowUp'].includes(e.key)){e.preventDefault();setDirectoryTool('category');}}} onClick={()=>setDirectoryTool(current=>current==='category'?null:'category')}><ListFilter size={20} aria-hidden="true"/>{category!=='全部詞彙'&&<span className="directory-tool-dot" aria-hidden="true"/>}</button></div>}<div className="header-actions">{user && <Notifications items={notifications} onOpen={openNotification} />}<PersonalMenu user={user} canEdit={canEdit} preference={preference} chooseTheme={chooseTheme} api={api} onOpen={setContributions} onAdmin={() => {
          if (edit) {
            tell('請先儲存或取消目前的編輯。');
            return;
          }
          setAboutOpen(false);
          setAdminOpen(true);
          setDiscussionOpen(false);
          const u = new URL(location.href);
          u.searchParams.set('view', 'admin');
          historyPush(u);
        }} onLogout={async () => {
          try {
            await api('logout', 'POST');
            localStorage.removeItem(sessionKey);
            setContributions(null);
            await refresh();
          } catch (e) {
            tell(e.message);
          }
        }} />{!user && <button className="primary small header-login" aria-label={remote.telegramReady || remote.discordReady ? '登入參與' : '登入未啟用'} onClick={() => setModal('login')}><LogIn size={15} aria-hidden="true" /><span className="header-login-full">{remote.telegramReady || remote.discordReady ? '登入參與' : '登入未啟用'}</span><span className="header-login-short" aria-hidden="true">{directoryToolsVisible?'登入':remote.telegramReady || remote.discordReady ? '登入' : '登入未啟用'}</span></button>}</div></header>
    {directoryToolsVisible&&<div className="directory-tools-host" id="directory-tools-panel" ref={setDirectoryToolsHost}/>}
    {aboutOpen ? <DeferredSection label="關於頁"><AboutPage onBack={goHome} /></DeferredSection> : adminOpen ? <DeferredSection label="成員管理"><AdminPage user={user} api={api} onBack={goHome} onChanged={refresh} /></DeferredSection> : isDirectory ? <Directory mobile={screenWidth<=900} tool={directoryTool} setTool={setDirectoryTool} toolsHost={directoryToolsHost} searchTrigger={directorySearchTrigger} categoryTrigger={directoryCategoryTrigger} onArranging={setDirectoryArranging} disclosure={directoryDisclosure} setDisclosure={setDirectoryDisclosure} catalog={remote.categories} api={api} onCategoriesChanged={refresh} onAbout={openAbout} onHistory={showHistory} canEdit={canEdit} notifications={notifications} onDiscuss={(t,tab='chat') => {
      if(directoryRef.current){directoryScroll.current=directoryRef.current.scrollTop;directoryReturnTerm.current=t.id;}
      workspace.navigate({type:'term',term:t,conversation:tab});
    }} onCreate={() => ensureEditor(() => {
      setManageError('');
      setModal('create-term');
    })} onDeleted={showDeleted} telegramReady={remote.telegramReady} terms={terms} filtered={filtered} comments={comments} categories={categories} query={query} setQuery={setQuery} category={category} setCategory={setCategory} filter={filter} setFilter={setFilter} chooseTerm={chooseTerm} scrollRef={directoryRef} /> : historyOpen ? <main className="revisions-page"><header className="revisions-page-heading"><button onClick={closeHistory}><ArrowLeft size={16} />{historyOrigin === 'directory' ? '返回總表' : '返回條目'}</button><h1>{term.label}<span>修訂歷史</span></h1></header><div className="history-content"><div className="history-tabs" role="group" aria-label="紀錄類型"><button aria-pressed={historyView === 'versions'} onClick={() => setHistoryView('versions')}>版本</button><button aria-pressed={historyView === 'activity'} onClick={() => setHistoryView('activity')}>活動紀錄</button></div>{historyLoading ? <p role="status">載入中…</p> : historyError ? <p role="alert">{historyError}<button onClick={() => {
            setHistoryOpen(false);
            setTimeout(() => setHistoryOpen(true), 0);
          }}>重試</button></p> : historyView === 'versions' ? <><DeferredSection label="版本紀錄"><VersionHistory comments={termComments} api={api} onDiscuss={openReviewDiscussion} key={term.id} terms={terms} term={term} events={history} user={canEdit ? user : null} onRestore={restoreVersion} /></DeferredSection></> : <><div className="history-term"><strong>{term.label}</strong><span>目前第 {term.version} 版</span></div>{history.length ? history.map(h => <div className="history-event" key={h.id}><span className="event-dot" /><div><header><strong>{h.author.name}</strong><span>{formatTime(h.createdAt)}</span></header>{h.summary && <p>修改摘要：{h.summary}</p>}<p>{h.type.startsWith('term-') ? {
                  'term-create': '新增詞彙',
                  'term-delete': '刪除詞彙',
                  'term-restore': '復原詞彙'
                }[h.type] : h.type === 'edit' ? (h.restoredFrom ? '還原第 ' + h.restoredFrom + ' 版 · ' : '更新詞彙 · ') + '第 ' + h.before.version + ' 版 → 第 ' + h.after.version + ' 版' : h.type.startsWith('thread-') ? ({'thread-delete':'刪除討論串','thread-restore':'還原討論串','thread-issue-link':'連結討論串到議題','thread-issue-unlink':'取消議題連結'}[h.type]||'討論串管理') : h.type === 'comment-restore' ? '還原留言' : h.type === 'comment-delete' ? '刪除留言' : h.type === 'comment' ? '新增討論留言' : h.type==='poll-delete'?'刪除議題':h.type==='poll-restore'?'還原議題':h.type.startsWith('poll-') ? '投票紀錄' : h.type.startsWith('name-') ? '譯名紀錄' : h.before?.resolved !== h.after?.resolved ? h.after.resolved ? '記錄暫定結論' : '重新開啟討論' : '編輯留言'}</p>{h.type.startsWith('term-') ? <p>{h.after.label}</p> : h.type === 'edit' ? <details><summary>查看變更內容</summary>{Object.keys(fieldNames).filter(k => h.before[k] !== h.after[k]).map(k => <div className="change-field" key={k}><b>{fieldNames[k]}</b><del>{k==='classification'?classificationSummary(h.before):h.before[k] || '（空白）'}</del><ins>{k==='classification'?classificationSummary(h.after):h.after[k] || '（空白）'}</ins></div>)}</details> : <details><summary>查看紀錄</summary>{h.type === 'comment' ? <p>{h.comment.body}</p> : h.type.startsWith('thread-') ? <p>{h.event?.issue?.question||'管理討論串，保留歷史紀錄。'}</p> : h.type.startsWith('poll-')||h.type.startsWith('name-') ? <p>{h.event?.reason||h.proposal?.reason||''}</p> : <><p>修改前：{h.before?.body}</p><p>修改後：{h.after?.body}</p>{h.after?.conclusion && <p>結論：{h.after.conclusion}</p>}</>}</details>}</div></div>) : <div className="history-empty"><History size={28} /><h3>從這個版本開始累積。</h3><p>目前只有原始匯入資料。之後的編輯與討論會記錄在這裡。</p></div>}<div className="history-initial"><FileText size={16} />原始資料匯入 · 所有名稱皆待討論</div></>}</div></main> : <>

    <main ref={workspaceViewport} onFocusCapture={focusWorkspacePane} style={{'--vote-width':voteWidth+'px',...(discussionWidth?{'--discussion-width':discussionWidth+'px'}:{})}} className={'workspace wiki-workspace three-pane ' + (namesVisible?'names-open ':'')+(rightOpen ? 'discussion-open ' : '')+(threadDetailOpen?'thread-detail-open ':'')+'pane-'+currentPane}>

      <nav className="workspace-destinations" aria-label="工作區"><button ref={articleTabRef} aria-pressed={currentPane==='article'} onClick={()=>showPane('article')}>{edit?'編輯':'文章'}</button><button aria-pressed={currentPane==='names'} onClick={()=>showPane('names')}>投票 <span>{polls.items.length||1}</span></button><button aria-pressed={currentPane==='discussion'&&conversationTab!=='ai'} onClick={()=>{if(conversationTab==='ai')setConversationTab('chat');showPane('discussion');}}>討論 <span>{topicCount} 串</span></button>{edit&&canEdit&&<button aria-label="開啟 AI 輔助" aria-pressed={currentPane==='discussion'&&conversationTab==='ai'} onClick={openEditorAI}><Sparkles size={16} aria-hidden="true"/>AI</button>}</nav>
      <aside id="translation-panel" className="translation-panel" hidden={!namesVisible} aria-label="投票面板"><VotePanelResize onChange={setVoteWidth}/><header><h2 ref={pollHeadingRef} tabIndex={-1}>投票</h2><button className="icon-button" aria-label="收合投票面板" onClick={closePollPanel}><X size={18}/></button></header><div className="translation-panel-scroll" ref={namesRef} onScroll={e=>{if(namesVisible)workspace.saveScroll('names',e.currentTarget.scrollTop);}}><PollPanel issueLinkState={issueLinks} onManageIssue={(mode,poll)=>setIssueManagement({mode,poll})} issueLinks={issueLinks.items} onOpenLinkedThread={openLinkedThread} onSourceDiscussion={openSourceDiscussion} key={term.id} term={term} user={user} canEdit={canEdit} api={api} data={polls} scope={currentPollScope} onScope={changePollScope} visible={namesVisible} reveal={pollReveal} resume={pollResume} onLogin={loginForPoll} onDiscuss={openPollDiscussion} onRefresh={refresh} comments={termComments} onRevision={(version,previous)=>showHistory(term,version,previous)} onEdit={p=>beginEdit(null,termComments.find(c=>c.id===p.commentId))}><NameProposals onSourceDiscussion={openSourceDiscussion} key={term.id+':'+user?.id} chooser sidebar proposeRequest={nameProposeRequest} onProposeHandled={()=>setNameProposeRequest(0)} reveal={nameReveal?.termId===term.id?nameReveal:null} visible={namesVisible&&currentPollScope==='names'} term={term} user={user} canEdit={canEdit} api={api} data={nameProposals} onRefresh={refresh} onLogin={loginForName} comments={termComments} activeId={discussionOpen?activeRoot?.id:null} onChoose={openNameDiscussion}/></PollPanel></div></aside>
      <section hidden={!articleVisible} onBlurCapture={rememberEditorFocus} className="article-pane wiki-article-pane" aria-label="詞彙條目">
      <div className="article-title-actions" role="group" aria-label="條目協作"><TermNavigation className="edge-term-navigation" hidden={!compactTitle&&!edit} term={term} {...neighbors} titleVisible={compactTitle||Boolean(edit)} onNavigate={t=>chooseTerm(t,false,true)}/><button hidden={namesVisible} ref={pollToggleRef} className="poll-panel-toggle" aria-label="議題與投票" title={namesVisible?"收合議題與投票":"開啟議題與投票"} aria-expanded={namesVisible} aria-controls="translation-panel" onClick={togglePollPanel}><span aria-hidden="true">{namesVisible?<PanelLeftClose size={17}/>:<PanelLeftOpen size={17}/>}</span><span>議題與投票</span></button>{edit&&canEdit&&<button className="editor-ai-toggle" aria-label="開啟 AI 輔助" aria-expanded={discussionOpen&&conversationTab==='ai'} aria-controls="article-discussion-panel" onClick={openEditorAI}><Sparkles size={16} aria-hidden="true"/><span>AI</span></button>}<button hidden={rightOpen} ref={discussionToggleRef} className={'discussion-toggle ' + (discussionOpen ? 'active' : '')} aria-expanded={discussionOpen} aria-controls="article-discussion-panel" onClick={()=>{if(conversationTab==='ai')setConversationTab('chat');toggleDiscussionPanel();}}><MessageCircle size={16} />討論 <span>{topicCount} 串</span></button></div>
        <div className={"article-scroll wiki-reader reading-shell-" + reading} id="wiki-top" ref={articleRef} onScroll={e => {if(articleVisible)workspace.saveScroll('article',e.currentTarget.scrollTop);setSelection(null);}} onMouseUp={captureSelection} onTouchEnd={() => setTimeout(captureSelection, 50)} onKeyUp={captureSelection}>

      <header className="term-heading article-title-bar workspace-title"><div className="article-title-text"><h1 lang="en" tabIndex={-1}>{term.english || term.label}</h1><button className="icon-button copy-entry" onClick={copyLink} title="複製條目連結" aria-label="複製條目連結">{toast === '已複製連結。' ? <Check size={16} /> : <LinkIcon size={16} />}</button></div><TermNavigation className="heading-term-navigation" term={term} {...neighbors} showTitle={false} inactive={compactTitle} onNavigate={t=>chooseTerm(t,false,true)}/></header>

<div className={"workspace-bar wiki-navigation"+(compactTitle?" has-compact-title":"")}><TermNavigation className="mobile-term-navigation" hidden={!compactTitle} term={term} {...neighbors} titleVisible={compactTitle} onNavigate={t=>chooseTerm(t,false,true)}/><div className="reading-controls" aria-label="閱讀語言"><div className="reading-modes" role="group" aria-label="閱讀模式">{[['zh', '中文草稿'], ['en', 'English'], ['split', '中英對照']].map(([value, label]) => <button key={value} aria-pressed={reading === value} disabled={Boolean(edit)} onClick={() => {
                    setReading(value);
                    setSelection(null);
                  }}>{label}</button>)}</div><select className="mobile-reading-select" aria-label="閱讀語言" value={reading} disabled={Boolean(edit)} onChange={e => {
                  setReading(e.target.value);
                  setSelection(null);
                }}><option value="zh">中文草稿</option><option value="en">English</option><option value="split">中英對照</option></select></div><div className="workspace-actions"><details className="mobile-toc"><summary>目次</summary><nav aria-label="文章目次">{mobileEnglish ? <><button onClick={e => {
                        e.currentTarget.closest('details').open = false;
                        jumpSection('english-article-start');
                      }}>Overview</button>{(english?.headings || []).map(h => <button key={h.id} onClick={e => {
                        e.currentTarget.closest('details').open = false;
                        jumpSection(h.id);
                      }}>{h.title}</button>)}</> : sections.map(([anchor, label]) => <button key={anchor} onClick={e => {
                      e.currentTarget.closest('details').open = false;
                      jumpSection(anchor);
                    }}>{label}</button>)}</nav></details><button className="history-entry" onClick={() => showHistory()}><History size={15} /><span>版本紀錄</span></button>{canEdit && <button className="edit-entry" onClick={() => beginEdit()} aria-label="編輯條目"><Pencil size={15} />編輯</button>}</div></div>
          {reading === 'split' && <div className="mobile-compare-switch" role="group" aria-label="中英對照語言"><button aria-pressed={splitLanguage === 'en'} onClick={() => switchSplitLanguage('en')}>English</button><button aria-pressed={splitLanguage === 'zh'} onClick={() => switchSplitLanguage('zh')}>中文草稿</button></div>}<div className={"bilingual-reading reading-" + reading + ' split-show-' + splitLanguage}>{reading !== 'zh' && <EnglishReference comments={roots} active={activeRoot?.id} onSelect={focusThread} source={english} loading={englishLoading} error={englishError} term={term} onRetry={() => {
                setEnglishError('');
                setEnglishLoading(true);
                api('terms/' + term.id + '/english').then(setEnglish).catch(e => setEnglishError(e.message)).finally(() => setEnglishLoading(false));
              }} />}<div className="wiki-document-layout">{!edit&&<nav className="wiki-toc" aria-label="條目目次"><h2>目次</h2>{sections.map(([anchor, label]) => <a key={anchor} href={'#' + anchor} onClick={e => {
                    e.preventDefault();
                    jumpSection(anchor);
                  }}>{label}</a>)}</nav>}<article ref={chineseDocumentRef} className={"wiki-document"+(compactChinese?" compact-prose":"")} id="chinese-article-start">
          {!edit&&<div className="article-name-summary"><div className="article-name-heading"><div className="article-translation"><h2>{term.label}</h2>{term.nameDecision?.label===term.label&&term.nameDecision?.confirmed&&<span className="translation-status">已確認</span>}</div><ConceptLinks term={term} onEdit={canEdit?()=>beginEdit('concept'):undefined}/></div><div className="translation-preview">{previewNames.length>0&&<>{previewNames.map(p=><button key={p.id} className="translation-preview-name" aria-controls="translation-panel" onClick={()=>openNames(p)}>{p.name}</button>)}</>}<button className="translation-preview-all" aria-controls="translation-panel" onClick={()=>openNames()}>查看譯名與別名</button></div></div>}
          {!edit&&<ConceptMatches term={term} terms={terms} onNavigate={chooseTerm}/>}


          {savedDrafts.length > 0 && <details className="saved-drafts"><summary>未送出草稿（{savedDrafts.length}）</summary>{savedDrafts.map((d, i) => <button key={i} onClick={() => {
                      setActive(d.composer.parentId || null);
                      openComposer(d.composer);
                    }}>繼續{d.composer.type === 'reply' ? '回覆' : '留言'}：{d.text.slice(0, 36)}</button>)}</details>}
          {!edit && reading !== 'split' && discussionHint()}
          {edit ? <DeferredSection label="編輯器"><WikiEditor draft={articleDraft} aiHost={aiHost} aiVisible={rightOpen&&conversationTab==='ai'} api={api} canUseAI={canEdit} saveHost={saveHost} categories={remote.categories} discussions={indexDiscussion(termComments).topics.map(x=>x.root).filter(c=>!c.deletedAt)} leaveRequest={leaveRequest} onKeepEditing={()=>{pendingNavigation.current=null;}} onDelete={() => ensureEditor(() => {
                      setManageError('');
                      setModal('delete-term');
                    })} key={term.id + ':' + editSection} edit={edit} setEdit={next=>{setEditError('');setEdit(next);}} error={editError} original={edit.baseSnapshot||term} terms={terms} initialSection={editSection} initialConcept={edit.editConcept===true} initialInfobox={edit.editInfobox===true} saving={saving} conflict={conflict} onSubmit={saveEdit} onCancel={leaveEditor} onMerge={values => {
                      articleDraft.merge(values, conflict);
                      setConflict(null);
                    }} /></DeferredSection> : <>
          {term.contentFormat === 'wikitext-v1' ? <>{reading !== 'split' && !compactChinese && <ArticleInfobox term={term} onEdit={canEdit?()=>beginEdit('infobox'):undefined}/>}<WikiContent afterLead={reading === 'split'||compactChinese ? <div className="reader-infobox-block"><ArticleInfobox term={term} onEdit={canEdit?()=>beginEdit('infobox'):undefined}/>{reading==='split'&&discussionHint()}</div> : undefined} source={term.wikitext || ''} terms={terms} comments={roots.filter(c => ['wikitext', 'article', 'definition', 'body'].includes(c.anchor?.field))} active={activeRoot?.id} onComment={focusThread} onNavigate={chooseTerm} /></> : <>
          <div className="wiki-intro-flow">{reading !== 'split' && !compactChinese && <ArticleInfobox term={term} onEdit={canEdit?()=>beginEdit('infobox'):undefined}/>}
          <section className="wiki-lead" aria-label="序言">{term[articleField] ? <>{field(term[articleField], articleField, 'prose')}<div className="lead-provenance">原稿出處：{term.sources.map((source, i) => source.url ? <a key={i} href={'#reference-' + i} onClick={e => {
                                e.preventDefault();
                                jumpSection('reference-' + i);
                              }}>[{i + 1}]</a> : null)}<span>內容仍待查證與修訂</span></div></> : <div className="wiki-stub"><p><b>{term.label}</b>（{term.english}）是本詞彙表收錄的待編寫條目。</p><p>目前已收錄候選名稱，導言及參考資料尚待補充。</p></div>}</section>
          {(reading === 'split'||compactChinese) && <div className="reader-infobox-block"><ArticleInfobox term={term} onEdit={canEdit?()=>beginEdit('infobox'):undefined}/>{reading==='split'&&discussionHint()}</div>}
          </div>
          {term.definition && term.article && term.definition !== term.article && <section className="wiki-section" id="wiki-overview"><h2>概述</h2>{field(term.definition, 'definition', 'prose')}</section>}
          {bodySections.map((section, i) => section.text.trim() && <section className="wiki-section" id={'body-section-' + i} key={i}><h2>{term.body !== undefined && section.titleStart !== null ? field(section.title, 'body', 'section-title', section.titleStart) : section.title}</h2>{section.text.trim() ? field(section.text, 'body', 'prose', section.start) : <div className="section-unwritten"><span>此節尚未撰寫。</span></div>}</section>)}{bodySections.some(s => !s.text.trim()) && <details className="unwritten-sections"><summary>待撰寫章節 · {bodySections.filter(s => !s.text.trim()).length}</summary>{bodySections.map((section, i) => !section.text.trim() && <div id={'body-section-' + i} key={i}><h2>{term.body !== undefined && section.titleStart !== null ? field(section.title, 'body', 'section-title', section.titleStart) : section.title}</h2></div>)}</details>}<section className="wiki-section" id="wiki-seealso"><h2>參見</h2><ul>{terms.filter(t => t.category === term.category && t.id !== term.id).map(t => <li key={t.id}><a href={'?term=' + encodeURIComponent(t.id)} onClick={e => {
                              if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                              e.preventDefault();
                              chooseTerm(t);
                            }}>{t.label}</a><span>（{t.english}）</span></li>)}</ul></section>
          <section className="wiki-section sources" id="wiki-references"><h2>參考資料</h2>{term.sources.map((s, i) => <div className="source-item" id={"reference-" + i} key={i}><span className="source-number">{i + 1}</span><div>{s.url ? <a href={s.url} target="_blank" rel="noreferrer">{s.title}<ArrowUpRight size={12} /></a> : <span>{s.title}</span>}<small>{s.label}</small></div></div>)}{term.references && <div className="references-text"><RichText text={term.references} /></div>}</section><section className="wiki-section" id="wiki-external"><h2>外部連結</h2>{term.wikiUrl ? <p><a href={term.wikiUrl} target="_blank" rel="noreferrer">維基百科對應條目 ↗</a></p> : <p className="empty-inline">尚未補充外部連結。</p>}</section><section className="wiki-section" id="wiki-names"><h2>名稱與別名</h2><p>本詞彙的英文名稱為 <b>{term.english}</b>，目前提出的中文名稱為「{term.label}」。</p>{term.aliases ? <><p>別名：</p>{field(term.aliases, 'aliases', 'aliases')}</> : <p className="empty-inline">尚未設定別名。</p>}<p className="wiki-draft-note">以上用語尚待討論，可選取名稱提出意見。</p></section>
          <section className="wiki-section" id="wiki-data"><h2>條目資料</h2><dl className="wiki-data-list"><dt>zh-tw 標籤</dt><dd>{term.label}</dd><dt>簡短描述</dt><dd>{term.description ? field(term.description, 'description', 'prose') : <span className="empty-inline">尚待撰寫</span>}</dd><dt>對應項目</dt><dd>{term.qid ? <a href={'https://www.wikidata.org/wiki/' + term.qid} target="_blank" rel="noreferrer">{term.qid}<ArrowUpRight size={12} /></a> : <span className="empty-inline">尚未確認 QID</span>}</dd></dl></section>
          </>}<ReviewPanel key={term.id + ':' + term.version + ':' + user?.id} term={term} api={api} user={user} onLogin={() => setModal('login')} onChanged={refresh} onDiscuss={openReviewDiscussion} /><footer className="article-footer"><span>{term.updatedBy?.name || '原始清單匯入'}</span><button onClick={showHistory}>查看紀錄<History size={12} /></button></footer></>}
          </article></div></div>
        </div>
        {selection && !edit && createPortal(<div ref={selectionToolbarRef} role="toolbar" aria-label="選取文字操作" className="selection-bar wiki-selection" style={selectionPoint || {}}><button className="opinion-action" onClick={() => startThread(selection)}><MessageCircle size={14} />提出意見</button></div>, document.body)}
        {!selection && !edit && <div className="article-bottom"><TextSelect size={14} /><span>反白文字，讓想法有個起點。</span></div>}
      </section>
      <section id="discussion-detail-panel" className="discussion-detail-pane chat-panel" ref={setDiscussionDetailHost} hidden={!threadDetailOpen} aria-label="討論內容"/>
      <section id="article-discussion-panel" style={rightOpen ? undefined : {
          display: 'none'
        }} className="discussion-pane article-discussions threads-pane chat-panel" aria-label="聊天室">
        {!threadDetailOpen&&<PanelResize width={discussionWidth} onChange={setDiscussionWidth}/>}
        <div className="right-chat-content">
        <ChatPanel issueLinkState={issueLinks} issueLinks={issueLinks.items} onOpenIssue={openLinkedIssue} onManageThread={(mode,root)=>{if(mode==='vote'){setVoteSource({source:root,root});return;}setThreadManagement({mode,root});}} onReturnToArticle={returnToArticle} detailHost={discussionDetailHost} voteSources={discussionVoteSources} onViewVote={viewDiscussionVote} onProposeVote={(source,root)=>{if(!user){setModal('login');return;}setVoteSource({source,root});}} aiEnabled={Boolean(edit)&&canEdit} aiHostRef={setAIHost} pollFocus={pollFocus} onBackToPoll={returnToPoll} nameFocus={nameDiscussionFocus?.termId===term.id?nameDiscussionFocus:null} onBackToNames={returnToNames} nameProposals={nameProposals} onEditDiscussion={c=>beginEdit(null,c)} onRevision={(version,previous) => showHistory(term, version, previous)} chosenTab={conversationTab} notices={notifications} onNew={pageThread} visible={rightOpen} key={term.id + ':' + user?.id} term={term} comments={termComments} user={user} canEdit={canEdit} active={active} onSelect={selectComment} onClose={closeDiscussionPanel} quoteStatus={c=>quoteLocation(term,c.anchor,english)} onQuote={gotoQuote} onTarget={gotoChinese} onDelete={deleteComment} onResolve={c => ensureWrite(() => {
            setActive(c.id);
            c.resolved ? setResolution(c, false) : setModal('resolve');
          })} expandedMessages={expandedMessages} onExpanded={expandMessage} api={api} onRefresh={refresh} intent={composer} onClearIntent={() => {
            setComposer(null);
                    setSelection(null);
          }} onLogin={() => setModal('login')} following={activeNotice?.following} onFollow={user ? toggleFollow : null} onRead={async () => {
            if (!user) return;
            if (activeRoot) return readThread(activeRoot);
            const notice = notifications.find(n => n.rootId === 'chat:' + term.id);
            if (notice?.unread) {
              try {
                await api('threads/' + encodeURIComponent(notice.rootId) + '/read', 'POST', {
                  seen: notice.latest
                });
                await refreshNotifications();
              } catch {}
            }
          }} scrollPositions={panelScroll} />
        </div>
      </section>
      {edit&&<div className="editor-save-host" ref={setSaveHost}/>}
    </main></>}
    {issueManagement&&user?.role==='admin'&&<IssueManagement key={term.id+':'+issueManagement.mode+':'+issueManagement.poll?.id} {...issueManagement} term={term} api={api} onClose={()=>setIssueManagement(null)} onChanged={async()=>{issueLinks.reload();polls.reload();const updated=await refresh();return Boolean(updated);}} onDeleted={(poll,result)=>{polls.remove(poll.id);if(currentPollScope===poll.id)changePollScope(null);tell(result.fresh?'議題已刪除，可在「已刪除議題」中還原。':'議題已刪除；其他內容尚未更新，請重試載入。');requestAnimationFrame(()=>pollHeadingRef.current?.focus({preventScroll:true}));}}/>}
    {threadManagement&&user?.role==='admin'&&<ThreadManagement key={term.id+':'+threadManagement.mode+':'+threadManagement.root?.id} {...threadManagement} term={term} api={api} onClose={()=>setThreadManagement(null)} onChanged={async change=>{issueLinks.apply(change);if(change?.operation==='delete'){stateRequest.current.invalidate();setRemote(current=>current?{...current,comments:projectThreadDeletion(current.comments,{rootId:change.rootId||threadManagement.root.id,termId:threadManagement.root.termId,affected:change.affected})}:current);polls.removeTargets(change.affected);nameProposals.removeTargets(change.affected);const hiddenPolls=new Set((change.affected?.polls||[]).map(item=>item.id)),hiddenOptions=new Set((change.affected?.options||[]).map(item=>item.id)),hiddenNames=new Set((change.affected?.candidates||[]).map(item=>item.id));setDiscussionVoteSources(current=>current.filter(source=>!hiddenPolls.has(source.target?.pollId)&&!hiddenOptions.has(source.target?.optionId)&&!hiddenNames.has(source.target?.proposalId)));}const [updated,linksUpdated]=await Promise.all([refresh(),issueLinks.reload()]);polls.reload();nameProposals.reload();return Boolean(updated)&&linksUpdated;}} onDeleted={(root,result)=>{setRemote(current=>{if(!current)return current;return {...current,comments:projectThreadDeletion(current.comments,{rootId:root.id,termId:root.termId,affected:result?.change?.affected})};});if(active===root.id||activeRoot?.id===root.id){selectComment(null,'threads');}tell(result?.fresh===false?'討論串已刪除；其他內容尚未更新，請重試載入。':'討論串已刪除，可在「已刪除」中還原。');requestAnimationFrame(()=>document.querySelector('.chat-list-tools button')?.focus({preventScroll:true}));}}/>}
    {voteSource&&<DiscussionToPoll key={voteSource.source.id+':'+voteSource.source.version} {...voteSource} term={term} api={api} onRefresh={async()=>{const fresh=await refresh();polls.reload();nameProposals.reload();return fresh;}} onClose={()=>setVoteSource(null)} onComplete={viewDiscussionVote}/>}
    {error && <div className="connection-error" role="alert">{error}<button onClick={refresh}>重試</button></div>}
    {savedEdit&&!edit&&!historyOpen&&selectedId===savedEdit.term.id&&<div className="editor-save-success" role="group" aria-label="儲存結果"><span role="status"><Check size={16}/>已儲存</span><button onClick={()=>{showHistory(savedEdit.term,savedEdit.term.version,savedEdit.previous);setSavedEdit(null);}}>查看變更</button><>{savedEdit.hasDiscussions&&<button onClick={()=>{setConversationTab('threads');setActive(null);setDiscussionOpen(true);setSavedEdit(null);}}>查看討論</button>}</><button className="icon-button" aria-label="關閉儲存結果" onClick={()=>setSavedEdit(null)}><X size={16}/></button></div>}
    {toast && <div className="toast" role="status"><Info size={17} />{toast}<button onClick={() => setToast('')} aria-label="關閉提示"><X size={14} /></button></div>}
    {modal === 'create-term' && <Modal title="新增詞彙" onClose={() => {
      if (!saving) setModal(null);
    }}><form className="term-form" onSubmit={createTerm}><label>英文<input name="english" required maxLength={200} autoFocus /></label><label>中文名稱<input name="label" required maxLength={200} /></label><label>分類<select name="category" defaultValue={category === '全部詞彙' ? categories[1] : category}>{categories.slice(1).map(c => <option key={c}>{c}</option>)}</select></label><label>文字<textarea name="article" rows={4} maxLength={30000} placeholder="可先留空，之後再編寫。" /></label>{manageError && <p role="alert">{manageError}</p>}<button className="primary" disabled={saving}>{saving ? '新增中…' : '新增'}</button></form></Modal>}
    {modal === 'delete-term' && <Modal title="刪除詞彙" onClose={() => {
      if (!saving) setModal(null);
    }}><div className="term-form"><p>刪除「{term.label}」（{term.english}）？</p><p>將從總表移除，保留 {termComments.length} 則留言與編輯紀錄，可在「已刪除」復原。</p>{manageError && <p role="alert">{manageError}</p>}<div className="form-actions"><button disabled={saving} onClick={() => setModal(null)}>取消</button><button className="primary" disabled={saving} onClick={deleteTerm}>確認刪除</button></div></div></Modal>}
    {modal === 'deleted-terms' && <Modal title="已刪除詞彙" onClose={() => setModal(null)}><div className="term-form">{manageError && <p role="alert">{manageError}</p>}{!deletedTerms.length && !manageError && <p>沒有已刪除詞彙。</p>}{deletedTerms.map(t => <div className="deleted-term" key={t.id}><div><strong>{t.label}</strong><small>{t.english}</small></div><button disabled={saving} onClick={() => restoreTerm(t)}>復原</button></div>)}</div></Modal>}



    {modal === 'login' && <LoginDialog config={remote} pendingName={Boolean(pendingName)} pendingPoll={Boolean(pendingPoll)} pendingOpinion={Boolean(pendingOpinion)} onClose={() => {workspace.clearIntent('name');workspace.clearIntent('poll');clearOpinionIntent();setModal(null);}} onSession={async session => {
      localStorage.setItem(sessionKey, session);
      await refresh();
      setModal(null);
      tell('已登入測試帳號。');
    }} />}
    {user && contributions && <ContributionsPanel key={user.id + contributions} kind={contributions} api={api} onClose={() => setContributions(null)} onNavigate={(kind, item) => {
      if (edit) {
        tell('請先儲存或取消目前的編輯。');
        return;
      }
      const target = terms.find(t => t.id === item.termId);
      if (!target) {
        tell('此條目已移除。');
        return;
      }
      setContributions(null);
      if (kind === 'comments') {
        openNotification({
          termId: item.termId,
          rootId: item.rootId,
          firstUnread: item.id
        });
      } else {
        showHistory(target);
        const u = new URL(location.href);
        u.searchParams.set('oldid', item.before);
        u.searchParams.set('diff', item.after);
        window.history.replaceState(window.history.state, '', u);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    }} />}
    {modal === 'resolve' && selectedComment && <Modal title="記錄暫定結論" onClose={() => setModal(null)}><ResolutionForm term={term} comment={selectedComment} api={api} saving={saving} onSubmit={(conclusion,version)=>setResolution(selectedComment,true,conclusion,version)}/></Modal>}

  </div>;
}
function RichText({
  text
}) {
  return <>{String(text || '').split(/(https?:\/\/[^\s<>]+)/g).map((part, i) => /^https?:\/\//.test(part) ? <a key={i} href={part} target="_blank" rel="noreferrer">{part}</a> : <React.Fragment key={i}>{part}</React.Fragment>)}</>;
}
createRoot(document.getElementById('root')).render(<App />);
